/**
 * ScheduleMeetingModal — Section 3 of the agentic-AI improvements spec.
 * Inline modal for scheduling a meeting from the lead focus page (no
 * navigation away). Mirrors the Demo Bookings 3-step flow condensed
 * into one scrollable modal because the lead is already known.
 */
import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { X, Sparkles, Check, Loader2, Video, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../App";

const MEETING_TYPES = ["Product demo", "Discovery call", "Follow-up call", "Executive review", "Contract review", "Custom"];
const PRODUCTS = ["MDnet EHR", "Practice Mgmt (RevQ)", "RevQ AI Billing", "Advanced Primary Care Mgmt (APCM)", "Chronic Care Mgmt (CCM)", "Remote Patient Monitoring (RPM)", "Annual Wellness Visit (AWV)"];
// Iter 171.D (Danny — bugs #6 / #7): the Host rep dropdown was a 3-row
// stub. We now fetch real users from the backend and let the rep pick
// any colleague. Roles considered "host-capable" mirror the spec.
// Iter 177.3 (Lisa #80419fad Feb 2026) — extended with `demo_agent`
// and `bookings_agent` so the Demo Agents and Booking Agents teams
// surface in the Host Rep dropdown of the Schedule Meeting popup.
// They run plenty of customer-facing meetings and the omission was
// pure oversight.
// Iter 188.6 (Feb 2026) — added `call_agent`. The Call Center Agent
// team (9 active reps: Ioannis, Aashika, Mia, Jennifer, Destinee,
// Paula, April, Sean, Joseph) runs warm-handoff demo bookings and
// must be selectable as a meeting host. No backend enforcement
// changes required — `POST /meetings` accepts any user id as
// host_rep_id; M365/RC pre-flight already gates un-connected hosts.
const HOST_CAPABLE_ROLES = new Set([
  "super_admin", "crm_admin", "sales_manager", "sales_agent",
  "contracts_agent", "implementation_specialist",
  "demo_agent", "bookings_agent",
  "call_agent",
]);

// Iter 266 (Jul 2026) — meeting-link enforcement (Layer 1). Reps who
// skipped "Generate RC link" and left the field blank used to silently
// end up with a `rcv-stub-…` URL that 404s in RC when clicked. We now
// require a real conferencing URL BEFORE the modal will save. The
// whitelist covers the platforms reps actually use; extend as needed.
const CONFERENCING_HOST_PATTERNS = [
  /(^|\.)meetings\.ringcentral\.com$/i,
  /(^|\.)v\.ringcentral\.com$/i,
  /(^|\.)zoom\.us$/i,
  /(^|\.)zoom\.com$/i,
  /(^|\.)teams\.microsoft\.com$/i,
  /(^|\.)teams\.live\.com$/i,
  /(^|\.)meet\.google\.com$/i,
  /(^|\.)webex\.com$/i,
  /(^|\.)whereby\.com$/i,
  /(^|\.)gotomeeting\.com$/i,
  /(^|\.)bluejeans\.com$/i,
];
function validateMeetingLink(raw) {
  const s = (raw || "").trim();
  if (!s) return { ok: false, error: "Add a meeting link, or click Generate RC link." };
  // Belt-and-suspenders: reject our own stub URLs. Shouldn't ever be
  // pasted by a rep, but if they cut-and-pasted from an old invite we
  // want to fail loudly so they regenerate.
  if (/rcv-stub-/i.test(s)) {
    return { ok: false, error: "This is an old placeholder link. Click Generate RC link, or paste a fresh one." };
  }
  let u;
  try { u = new URL(s); } catch { return { ok: false, error: "That doesn't look like a valid URL." }; }
  if (u.protocol !== "https:" && u.protocol !== "http:") {
    return { ok: false, error: "Meeting link must be an https URL." };
  }
  if (!CONFERENCING_HOST_PATTERNS.some(re => re.test(u.hostname))) {
    return { ok: false, error: "Use a RingCentral / Zoom / Teams / Meet / Webex link." };
  }
  return { ok: true };
}

export default function ScheduleMeetingModal({ lead, onClose, onSave }) {
  const { user: meUser } = useAuth();
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  // Iter 181 (Feb 2026 — Ioannis `fc81881e`): the previous initialiser
  // only looked at `lead.contacts[0].name`, so leads loaded with their
  // contact data on `lead.decision_makers` or just `lead.contact_name`
  // landed with an empty `meetingWith`. Reps then hit Save and got the
  // confusing "Choose who you're meeting with" toast even though the
  // lead WAS selected. Resolve against the same chain the dropdown
  // uses (contacts → decision_makers → contact_name).
  // Iter 258 (Jun 2026) — EHI client fallback: the client detail page
  // hands us the primary contact on `profile.email`/`profile.phone` OR a
  // top-level `email`/`phone` (it has no `contacts[]`/`decision_makers[]`),
  // so "Meeting with" used to come up empty. Prefer email, then phone.
  const clientPrimaryContact =
    lead?.profile?.email || lead?.email || lead?.profile?.phone || lead?.phone || "";
  const initialMeetingWith = (() => {
    const fromContacts = (lead?.contacts || []).map(c => c?.name || c?.full_name || c?.email || c?.title).find(Boolean);
    if (fromContacts) return fromContacts;
    const fromDms = (lead?.decision_makers || []).map(c => c?.name || c?.full_name || c?.email || c?.title).find(Boolean);
    if (fromDms) return fromDms;
    if (lead?.contact_name) return lead.contact_name;
    return clientPrimaryContact;
  })();
  const [meetingWith, setMeetingWith] = useState(initialMeetingWith);
  // Iter 171.D (Danny — bug #6): Title / subject line for the meeting.
  // Free-text so reps can paste their preferred booking subject
  // (e.g. "CCM-EHI: Kamilla Ismailoff, MD — 06.12.2026 @ 03:00 pm EST").
  const [title, setTitle] = useState(() => {
    const ctype = lead?.scoring?.primary?.product || "";
    const clinic = lead?.clinic || lead?.company || "";
    return clinic ? `Meeting — ${clinic}${ctype ? ` (${ctype})` : ""}` : "";
  });
  const [date, setDate] = useState(lead?.preset_date || today);
  const [time, setTime] = useState(lead?.preset_time || "10:00");
  const [tz, setTz] = useState(lead?.timezone || "America/New_York");
  const [type, setType] = useState("Product demo");
  // Iter 171.D (Danny — bug #7): Host rep is now a SELECTABLE list of
  // every host-capable user. Default to "me" but the rep can pick any
  // colleague. `host` stores the user_id; `hostName` the display name.
  // Iter 175.7 (Lisa report `07be525c`): the previous load was hitting
  // `/api/users` (404) so the dropdown stayed at "Loading hosts…" for
  // every rep. Switched to the live `/api/users/directory` endpoint
  // which returns `{users:[…]}` with role + full_name.
  const [host, setHost] = useState("");
  const [users, setUsers] = useState([]);
  // Iter 175.7 (Lisa report `bad77ece`): when Meeting type === Custom
  // the rep needs a free-text field to describe what the meeting is
  // about. Renders right under the dropdown.
  const [customType, setCustomType] = useState("");
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/users/directory`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => {
      const rows = Array.isArray(r.data?.users) ? r.data.users
                 : Array.isArray(r.data) ? r.data
                 : (r.data?.items || []);
      // Iter 175.7 — alphabetize hosts by display name (Lisa's #3
      // request: "in alphabetical order").
      const filtered = rows
        .filter((u) => HOST_CAPABLE_ROLES.has((u.role || "").toLowerCase()))
        .sort((a, b) => String(a.full_name || a.email || "").localeCompare(
                              String(b.full_name || b.email || "")));
      setUsers(filtered);
      if (!host && filtered.length) {
        // Iter 178.2 (Lisa #7e855b8c Feb 2026): Host Name kept showing
        // "Rachael" (the alphabetical first user) instead of the
        // logged-in rep. Root cause — the prior code read
        // `localStorage.getItem("session")` which AuthContext never
        // writes; only `localStorage.token` exists. We now resolve "me"
        // via the live `useAuth()` user object, then fall back to
        // `id`-match by email if the id doesn't appear in the directory
        // payload (some directory rows ship without `id`).
        const meId = meUser?.id || null;
        const meEmail = (meUser?.email || "").toLowerCase();
        const me = filtered.find((u) => (meId && u.id === meId)
                                     || (meEmail && (u.email || "").toLowerCase() === meEmail));
        setHost((me || filtered[0]).id);
      }
    }).catch(() => { /* leaves host empty; user can still pick */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meUser?.id, meUser?.email]);

  // ── Iter 235 (July 2026) — Preflight readiness check ─────────────
  // Users complained "scheduling doesn't work": root cause was that
  // reps who never connected their M365 mailbox would successfully
  // save a meeting in the CRM but the backend's Outlook sync path
  // silently skipped (`organizer has no m365 tokens`) so attendees
  // never got a calendar invite. Same story on RC — Generate RC link
  // only works if the rep has connected their personal RC account.
  //
  // We now do a lightweight preflight when the modal opens: fetch
  // both integration statuses and, if either is missing, render a
  // red banner + disable the affected actions with a "Connect first"
  // CTA that deep-links to Settings.
  const [preflight, setPreflight] = useState({ loading: true, m365: null, rc: null });
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) { setPreflight({ loading: false, m365: null, rc: null }); return; }
    const headers = { Authorization: `Bearer ${token}` };
    const API = process.env.REACT_APP_BACKEND_URL;
    Promise.all([
      axios.get(`${API}/api/integrations/m365/oauth/status`, { headers })
        .then(r => r.data).catch(() => ({ configured: false, connected: false })),
      axios.get(`${API}/api/integrations/ringcentral/oauth/status`, { headers })
        .then(r => r.data).catch(() => ({ configured: false, connected: false })),
    ]).then(([m365, rc]) => {
      setPreflight({ loading: false, m365, rc });
    });
  }, []);
  const m365Ready = !!preflight.m365?.connected;
  const rcReady   = !!preflight.rc?.connected;
  // Only surface warnings for integrations the tenant has actually
  // wired at the platform level. If MS_CLIENT_ID (etc.) aren't set
  // on this deployment, `configured` is false — nothing the rep can
  // do about it, so we don't badger them.
  const m365NeedsRepAction = preflight.m365?.configured && !m365Ready;
  const rcNeedsRepAction   = preflight.rc?.configured && !rcReady;
  const anyRepGap = m365NeedsRepAction || rcNeedsRepAction;
  // Iter 175.7 (Lisa report `0617c181`): the prior default was a
  // FAKE RingCentral URL with a random session id that didn't
  // exist — anyone clicking it landed on the generic RC video
  // page. New default is blank with a placeholder prompting the
  // rep to paste a real meeting link from RC / Zoom / Teams.
  // (Auto-generating a real RC Video meeting via API is on the
  // roadmap — needs the RC Video subscription which the current
  // tenant doesn't carry.)
  const [link, setLink] = useState("");
  // Iter 266 — Layer 1: track inline validation state for the meeting
  // link so we can surface a red error under the field without a toast
  // (toasts get missed on tall modals; inline is stickier).
  const [linkError, setLinkError] = useState("");
  const [notes, setNotes] = useState("");
  const [products, setProducts] = useState(() => {
    if (lead?.products_pitched?.length) return [...lead.products_pitched];
    const inferred = lead?.scoring?.primary?.product;
    return inferred === "CCM" ? ["Chronic Care Mgmt (CCM)"] : inferred === "RPM" ? ["Remote Patient Monitoring (RPM)"] : ["MDnet EHR"];
  });
  const [showAITimes, setShowAITimes] = useState(false);
  const [drafting, setDrafting] = useState(false);
  // Iter 158 (Josh) — issue report #21 (ad002a65): reps need to
  // invite additional attendees (managers, colleagues, decision
  // makers) when scheduling demos. Comma- or newline-separated list
  // of emails; validated client-side, sent to backend as `cc_emails`.
  const [ccEmails, setCcEmails] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const needsClientEmail = !!lead?.require_client_email && !(lead?.email || "").includes("@");

  // Iter 178.2 (Lisa #3fe1558a Feb 2026): "Generate RC Meeting Link"
  // button calls the backend, which creates a real RingCentral Meeting
  // against the logged-in rep's OAuth token and returns the joinUri.
  // Previously the field auto-filled with a FAKE random RC URL that
  // landed invitees on RC's generic portal — Iter 175.7 removed that.
  // We never bring back the fake. The button is enabled only when the
  // rep has a date+time and the host rep is resolved.
  const [generatingLink, setGeneratingLink] = useState(false);
  const generateMeetingLink = async () => {
    if (generatingLink) return;
    if (!date || !time) {
      toast.error("Pick a date and time first");
      return;
    }
    setGeneratingLink(true);
    try {
      // Build the ISO timestamp from the rep's date+time+tz selection.
      // RC expects UTC; we approximate by constructing a local Date
      // string and using `toISOString()` (the browser converts to UTC).
      // For a strict tz-aware conversion across timezones we'd need a
      // date-fns-tz dep; the current approach matches how the rest of
      // the modal interprets `date+time` for the calendar invite.
      const startLocal = new Date(`${date}T${time}:00`);
      const startIso = isNaN(startLocal.getTime()) ? null : startLocal.toISOString();
      const subject = (title && title.trim())
        || `Meeting — ${lead?.clinic || meetingWith || "Lead"}`;
      const token = localStorage.getItem("token");
      const r = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/integrations/rc/meetings/create`,
        {
          topic: subject,
          start_time_iso: startIso,
          duration_minutes: 60,
          time_zone: tz,
          lead_id: lead?.id || null,
        },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 20000 },
      );
      const joinUrl = r.data?.join_url;
      if (joinUrl) {
        setLink(joinUrl);
        toast.success("RingCentral meeting link generated");
      } else {
        toast.error("RC returned no join URL — paste a link manually.");
      }
    } catch (e) {
      const code = e?.response?.data?.code;
      const msg  = e?.response?.data?.message || e?.message || "RC error";
      const providerReason = e?.response?.data?.provider_reason;
      const providerErrCode = e?.response?.data?.provider_error_code;
      if (code === "rc_oauth_required") {
        toast.error("Connect your RingCentral in Settings → Connected Services first.");
      } else if (code === "rc_validation_error") {
        toast.error(msg);
      } else if (!e?.response) {
        // Iter 178.7 (Lisa #ffc40e55 Feb 2026): when the backend isn't
        // reachable (deploy mid-restart, timeout, gateway 502, CORS),
        // axios throws with no `response` and a generic "Network Error"
        // / "timeout of 20000ms exceeded". Show a clear, actionable
        // toast rather than the cryptic raw message.
        toast.error("Couldn't reach RC. Refresh the page (Ctrl+R / ⌘+R) and try again — if it keeps failing, your link may need to be pasted manually.");
      } else if (code === "rc_provider_error") {
        // Surface RC's actual reason (e.g., CMN-408 "Feature not
        // available for current account" = no RC Meetings subscription).
        // Iter 252 (Jul 2026): the RCV Bridges endpoint returns
        // `InsufficientPermissions` with the human message
        // "…needs [Video] permission" when either the RC OAuth app is
        // missing the Meetings/Video permission or the rep's token was
        // minted before that permission was enabled. Reconnect fixes
        // case (b); the admin has to enable it in the RC Dev Console
        // for case (a).
        const missingVideoPerm = (
          providerErrCode === "InsufficientPermissions" ||
          providerErrCode === "CMN-401"
        ) && /\bvideo\b/i.test(msg);
        if (missingVideoPerm) {
          toast.error(
            "RingCentral: Meetings permission is missing. Ask your admin to " +
            "enable the Meetings (Video) permission in the RingCentral " +
            "Developer Console → App → Auth → OAuth Settings, then reconnect " +
            "RC in Settings → Connected Services."
          );
        } else if (providerErrCode === "AGW-404") {
          toast.error(
            "RingCentral: the meeting endpoint returned 404. This usually " +
            "means the RC OAuth app is missing the Meetings/Video permission. " +
            "Please contact your admin."
          );
        } else {
          toast.error(`RingCentral: ${msg}${providerErrCode ? ` (${providerErrCode})` : ""}`);
        }
      } else {
        toast.error(`Couldn't generate link: ${msg}`);
      }
      if (providerReason) console.warn("[RC meeting] provider_reason:", providerReason);
    } finally {
      setGeneratingLink(false);
    }
  };

  // Iter 178.2 (Lisa #7e855b8c Feb 2026): Invite Others used to be a
  // free-text field only — Lisa explicitly asked for a dropdown of
  // teammates. We keep the free-text input (for external invitees like
  // a clinic's billing manager) AND add a multi-select of teammates
  // beside it. Selected teammate emails are merged with the free-text
  // emails on save.
  const [ccTeammates, setCcTeammates] = useState([]);   // array of user.id
  // Iter 253 (Jul 2026): the teammate picker used to render every user
  // in the tenant (30+ rows for mid-sized teams), unsorted, with no
  // search — impossible to find someone quickly. Now sorted A→Z with a
  // live search box that matches on name OR email.
  const [ccSearch, setCcSearch] = useState("");
  const toggleCcTeammate = (id) => {
    setCcTeammates((arr) => (arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]));
  };

  // Iter 178.2 — lint cleanup (react-hooks/purity): `useMemo` flagged
  // because `Date.now()` is impure. The slots are computed once at
  // mount-time and never recomputed, so a lazy-init `useState` is the
  // semantically-correct primitive here.
  const [aiTimes] = useState(() => {
    const tomorrow = new Date(Date.now() + 86400000);
    const dayAfter = new Date(Date.now() + 86400000 * 2);
    const wk = new Date(Date.now() + 86400000 * 7);
    return [
      { date: tomorrow.toISOString().slice(0, 10), time: "10:00", note: "82% historical show rate · Tue mornings" },
      { date: dayAfter.toISOString().slice(0, 10), time: "14:00", note: "75% show rate · early afternoon" },
      { date: wk.toISOString().slice(0, 10), time: "11:30", note: "Best for executive availability" },
    ];
  });

  // ── Iter 327 (Phase A+B) — Outlook free/busy for the selected host ──
  // Fetch the host's busy blocks from Graph via `/me/calendar/getSchedule`
  // whenever the host or date changes. Best-effort: if the scheduler
  // hasn't OAuth'd M365, or Graph returns an error, or the target is
  // outside the tenant, we simply hide the timeline — the modal
  // renders exactly like it did pre-Iter-327 (zero regression).
  const AVAIL_START_HOUR = 8;   // display window 08:00
  const AVAIL_END_HOUR = 20;    // display window 20:00
  const AVAIL_INTERVAL = 30;    // minutes
  const [busySlots, setBusySlots] = useState([]);   // [{start,end,status,subject?}]
  const [busyLoading, setBusyLoading] = useState(false);
  const [busyError, setBusyError] = useState(""); // "" | "not_connected" | "graph_error" | "external"
  const [hostWorkingHours, setHostWorkingHours] = useState(null);
  const hostUser = users.find((u) => u.id === host);
  const hostEmail = (hostUser?.email || "").trim().toLowerCase();
  useEffect(() => {
    // Guardrails: nothing to do if we don't have a host or date, OR the
    // scheduler hasn't connected M365. Silently skip.
    if (!hostEmail || !date || !m365Ready) {
      setBusySlots([]); setBusyError(hostEmail && date && !m365Ready ? "not_connected" : ""); setHostWorkingHours(null);
      return;
    }
    let cancelled = false;
    setBusyLoading(true); setBusyError("");
    const token = localStorage.getItem("token");
    // Build a full-day window in ISO local time; Graph interprets it in the tz we pass.
    const start_iso = `${date}T00:00:00`;
    const end_iso   = `${date}T23:59:59`;
    axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/api/integrations/m365/calendar/free-busy`,
      { emails: [hostEmail], start_iso, end_iso, time_zone: tz, interval_minutes: AVAIL_INTERVAL },
      { headers: { Authorization: `Bearer ${token}` }, timeout: 12000 },
    ).then((r) => {
      if (cancelled) return;
      const data = r?.data || {};
      if (!data.ok) {
        setBusyError(data.code || "graph_error"); setBusySlots([]); setHostWorkingHours(null);
        return;
      }
      const row = (data.results || []).find((x) => (x.email || "").toLowerCase() === hostEmail) || (data.results || [])[0];
      if (!row) { setBusySlots([]); setHostWorkingHours(null); return; }
      // Graph sometimes returns per-schedule errors when the email
      // isn't in the tenant OR the mailbox blocks free/busy. Surface a
      // soft banner but never block scheduling.
      if (row.error) { setBusyError("external"); setBusySlots([]); setHostWorkingHours(null); return; }
      setBusySlots(Array.isArray(row.busy) ? row.busy : []);
      setHostWorkingHours(row.workingHours || null);
    }).catch(() => {
      if (!cancelled) { setBusySlots([]); setBusyError("graph_error"); setHostWorkingHours(null); }
    }).finally(() => {
      if (!cancelled) setBusyLoading(false);
    });
    return () => { cancelled = true; };
  }, [hostEmail, date, tz, m365Ready]);

  // Compute the currently-selected slot's overlap with any busy block.
  const selectedConflict = useMemo(() => {
    if (!date || !time || busySlots.length === 0) return null;
    // Interpret selection as local time; compare in-place with the raw
    // Graph strings (Graph respects the `Prefer: outlook.timezone` we sent
    // so the strings are in the requested tz already).
    const selStart = new Date(`${date}T${time}:00`);
    const selEnd = new Date(selStart.getTime() + 30 * 60 * 1000);
    for (const b of busySlots) {
      const bs = new Date(b.start);
      const be = new Date(b.end);
      // Overlap when start < otherEnd && end > otherStart
      if (selStart < be && selEnd > bs) return b;
    }
    return null;
  }, [date, time, busySlots]);

  // ── Iter 328 (Teammates Time V2) — Outlook free/busy for the SELECTED
  // invite teammates. Mirrors the host fetch above but for the multi-
  // select picker. We request the same full-day window so changing only
  // the *time* doesn't refetch — the conflict memo below recomputes.
  // Graceful by design: if the scheduler isn't M365-connected the whole
  // feature stays silent (same as the host timeline). Per teammate, if
  // they're outside the tenant / hide free-busy / their entry errors,
  // they resolve to "unknown" (neutral grey) — never blocks scheduling.
  // Iter 346 — colour EVERY teammate currently shown in the expanded
  // picker (not just selected ones) so a rep can scan availability
  // before choosing whom to invite. We look up only the teammates
  // actually shown (search narrows the list); backend caps at 20 emails
  // so any overflow rows simply resolve to "unknown". Mirrors the render
  // filter (search on name/email) so the fetched set == the shown set.
  const shownTeammateEmails = useMemo(() => {
    const q = ccSearch.trim().toLowerCase();
    return Array.from(new Set(
      users
        .filter((u) => {
          if (!q) return true;
          const name = (u.full_name || u.name || "").toLowerCase();
          const email = (u.email || "").toLowerCase();
          return name.includes(q) || email.includes(q);
        })
        .map((u) => (u.email || "").trim().toLowerCase())
        .filter((e) => e.includes("@")),
    ));
  }, [users, ccSearch]);
  const [teammateBusy, setTeammateBusy] = useState({});   // { emailLower: {busy:[], error:bool} }
  const [teammateBusyLoading, setTeammateBusyLoading] = useState(false);
  useEffect(() => {
    // Skip entirely when there's nothing to look up — avoids needless
    // Graph calls (no teammates shown, no date, or scheduler not connected).
    if (shownTeammateEmails.length === 0 || !date || !m365Ready) {
      setTeammateBusy({}); setTeammateBusyLoading(false);
      return;
    }
    let cancelled = false;
    setTeammateBusyLoading(true);
    const token = localStorage.getItem("token");
    const start_iso = `${date}T00:00:00`;
    const end_iso   = `${date}T23:59:59`;
    axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/api/integrations/m365/calendar/free-busy`,
      { emails: shownTeammateEmails, start_iso, end_iso, time_zone: tz, interval_minutes: AVAIL_INTERVAL },
      { headers: { Authorization: `Bearer ${token}` }, timeout: 12000 },
    ).then((r) => {
      if (cancelled) return;
      const data = r?.data || {};
      const map = {};
      if (data.ok) {
        for (const row of (data.results || [])) {
          const em = (row.email || "").toLowerCase();
          if (!em) continue;
          map[em] = { busy: Array.isArray(row.busy) ? row.busy : [], error: !!row.error };
        }
      }
      // Emails absent from the map (e.g. partial failure, not_connected,
      // over the 20-email cap) are treated as "unknown" by the memo below.
      setTeammateBusy(map);
    }).catch(() => {
      if (!cancelled) setTeammateBusy({});
    }).finally(() => {
      if (!cancelled) setTeammateBusyLoading(false);
    });
    return () => { cancelled = true; };
  }, [shownTeammateEmails, date, tz, m365Ready]);

  // Availability per teammate email against the chosen 30-min slot.
  // Returns { [emailLower]: "free" | "busy" | "unknown" }. Only computed
  // when a date AND time are picked (nothing to compare against yet).
  const teammateAvailByEmail = useMemo(() => {
    const out = {};
    if (!date || !time) return out;
    const selStart = new Date(`${date}T${time}:00`);
    const selEnd = new Date(selStart.getTime() + 30 * 60 * 1000);
    for (const [em, rec] of Object.entries(teammateBusy)) {
      if (!rec || rec.error) { out[em] = "unknown"; continue; }
      let busy = false;
      for (const b of (rec.busy || [])) {
        const bs = new Date(b.start), be = new Date(b.end);
        if (selStart < be && selEnd > bs) { busy = true; break; }
      }
      out[em] = busy ? "busy" : "free";
    }
    return out;
  }, [teammateBusy, date, time]);


  // Precompute half-hour cells in the [AVAIL_START_HOUR, AVAIL_END_HOUR) window.
  const timelineCells = useMemo(() => {
    if (!date) return [];
    const cells = [];
    for (let h = AVAIL_START_HOUR; h < AVAIL_END_HOUR; h++) {
      for (const m of [0, 30]) {
        const start = new Date(`${date}T${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:00`);
        const end = new Date(start.getTime() + AVAIL_INTERVAL * 60 * 1000);
        let status = "free";  // free | busy | tentative | oof
        let subject = null;
        for (const b of busySlots) {
          const bs = new Date(b.start), be = new Date(b.end);
          if (start < be && end > bs) {
            // Prefer the most conservative status if two overlap.
            const rank = { free: 0, tentative: 1, workingElsewhere: 1, busy: 2, oof: 3 };
            if ((rank[b.status] || 2) > (rank[status] || 0)) {
              status = b.status || "busy"; subject = b.subject || subject;
            }
          }
        }
        cells.push({ time: `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`, status, subject });
      }
    }
    return cells;
  }, [date, busySlots]);


  const aiDraftAgenda = () => {
    setDrafting(true);
    setTimeout(() => {
      setNotes(`Agenda for ${type.toLowerCase()} with ${lead?.clinic || "the practice"}\n• Quick intro & confirm attendees\n• Walk through ${products.join(", ") || "Enable products of interest"}\n• Tailor to ${lead?.specialty || lead?.nppes?.specialty_group || "specialty"} workflow\n• Revenue math: APCM/CCM/RPM stack vs current setup\n• Q&A + define next step\n\nPrep: review ${lead?.activities?.[0]?.text || "recent activity"}; bring 2–3 peer references.`);
      setDrafting(false);
      toast.success("Agenda drafted by AI");
    }, 500);
  };

  const handleSave = () => {
    if (!meetingWith) {
      // Iter 181 (Feb 2026 — Ioannis `fc81881e`): give a useful nudge
      // when the lead has no contacts on file at all, instead of the
      // generic "choose…" toast that confused reps when the lead WAS
      // selected.
      const hasAnyContact = (lead?.contacts || []).length > 0
        || (lead?.decision_makers || []).length > 0
        || !!lead?.contact_name;
      toast.error(hasAnyContact
        ? "Pick a contact from the Meeting with dropdown"
        : "This lead has no contacts on file — add one in the Contacts tab first");
      return;
    }
    if (!date) { toast.error("Pick a date"); return; }
    if (needsClientEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clientEmail.trim())) {
      toast.error("Add the client's email so they get the booking confirmation and reminders");
      try { document.querySelector('[data-testid="m-client-email"]')?.focus(); } catch { /* noop */ }
      return;
    }
    // Iter 266 (Jul 2026) — Layer 1: enforce a real conferencing link
    // BEFORE persisting the meeting. Prior to this the backend fell
    // through to `StubRCVProvider` and manufactured a `rcv-stub-…` URL
    // that 404'd when clicked — reps would then send broken invites
    // to prospects.
    const linkCheck = validateMeetingLink(link);
    if (!linkCheck.ok) {
      setLinkError(linkCheck.error);
      toast.error(linkCheck.error);
      // Scroll the field into view so the rep can act on the error.
      try { document.querySelector('[data-testid="m-link"]')?.focus(); } catch { /* noop */ }
      return;
    }
    setLinkError("");
    // Iter 158 (Josh) #21 — parse + light-validate the additional invitees.
    const ccList = ccEmails
      .split(/[\s,;\n]+/)
      .map(s => s.trim())
      .filter(Boolean);
    const badEmail = ccList.find(s => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s));
    if (badEmail) { toast.error(`Invalid email: ${badEmail}`); return; }
    // Iter 178.2 (Lisa #7e855b8c) — merge teammate selections into the
    // cc list as proper emails. De-dupe so a rep who appears in both
    // the dropdown AND the free-text only gets one invite.
    const ccFromTeammates = ccTeammates
      .map((id) => users.find((u) => u.id === id))
      .filter(Boolean)
      .map((u) => (u.email || "").trim())
      .filter(Boolean);
    const mergedCcSet = new Set();
    const mergedCc = [];
    for (const e of [...ccFromTeammates, ...ccList]) {
      const key = e.toLowerCase();
      if (!mergedCcSet.has(key)) { mergedCcSet.add(key); mergedCc.push(e); }
    }
    // Iter 171.D (Danny — bugs #6 / #7): resolve host_user_id + host_name
    // from the selected user, and persist the rep-entered title.
    const hostUser = users.find((u) => u.id === host);
    // Iter 175.7 (Lisa #4): if Custom type was picked, require the
    // free-text description and append it to the persisted type/title
    // so the invitee + downstream calendar invite carry the context.
    if (type === "Custom" && !customType.trim()) {
      toast.error("Add a description for the Custom meeting type");
      return;
    }
    const effectiveType = type === "Custom" && customType.trim()
      ? `Custom — ${customType.trim()}`
      : type;
    onSave?.({
      title: title.trim() || `Meeting — ${lead?.clinic || "Lead"}`,
      meetingWith, date, time, tz,
      type: effectiveType,
      custom_type_description: type === "Custom" ? customType.trim() : null,
      host_user_id: hostUser?.id || host,
      host_name: hostUser ? (hostUser.full_name || hostUser.name || hostUser.email) : host,
      host, // legacy field — keep for any consumer reading the old key
      link, notes, products, cc_emails: mergedCc,
      client_email: needsClientEmail ? clientEmail.trim() : null,
    });
    const sentTo = lead?.contacts?.[0]?.email || "the lead";
    const cc = mergedCc.length ? ` and ${mergedCc.length} invitee${mergedCc.length === 1 ? "" : "s"}` : "";
    toast.success(`Meeting scheduled — invite sent to ${sentTo}${cc}`);
    onClose?.();
  };

  return (
    <div data-testid="meeting-modal" style={{ position: "fixed", inset: 0, zIndex: 110, background: "rgba(11,17,32,0.45)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px 24px 84px" }} onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div style={{ width: 560, maxWidth: "100%", maxHeight: "calc(100vh - 108px)", background: "#fff", borderRadius: 14, overflow: "hidden", boxShadow: "0 24px 60px -16px rgba(0,0,0,0.25)", display: "flex", flexDirection: "column" }}>
        <header style={{ padding: "14px 20px", borderBottom: "0.5px solid #eee", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: "var(--navy)" }}>Schedule a meeting — {lead?.clinic || "lead"}</h3>
          <button data-testid="meeting-close" onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer", color: "#888", padding: 4 }}><X style={{ width: 16, height: 16 }} /></button>
        </header>

        <div style={{ padding: "16px 20px", overflowY: "auto", flex: 1 }}>
          {/* Iter 235 (July 2026) — Preflight banner. Warns the rep
              BEFORE they book a meeting that will silently fail to
              sync to Outlook / generate a real RC link. Only renders
              when the tenant HAS the integration configured but this
              specific rep hasn't connected — so we don't spam reps
              at tenants where the feature isn't wired at all. */}
          {anyRepGap && (
            <div
              data-testid="m-preflight-banner"
              style={{
                background: "#FEF2F2",
                border: "0.5px solid #FCA5A5",
                borderRadius: 10,
                padding: "10px 12px",
                marginBottom: 14,
                display: "flex",
                gap: 10,
                alignItems: "flex-start",
              }}
            >
              <AlertTriangle style={{ width: 16, height: 16, color: "#B91C1C", marginTop: 1, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#991B1B", marginBottom: 4 }}>
                  Connect your accounts before scheduling
                </div>
                <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11, color: "#7F1D1D", lineHeight: 1.55 }}>
                  {m365NeedsRepAction && (
                    <li data-testid="m-preflight-m365">
                      <strong>Microsoft 365 not connected</strong> — attendees will NOT receive an Outlook calendar invite. Meeting saves in the CRM only.
                    </li>
                  )}
                  {rcNeedsRepAction && (
                    <li data-testid="m-preflight-rc">
                      <strong>RingCentral not connected</strong> — &quot;Generate RC link&quot; will fail. Paste a meeting link manually from RC / Zoom / Teams / Meet, or connect RC first.
                    </li>
                  )}
                </ul>
                <a
                  href="/settings?section=integrations"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="m-preflight-connect-cta"
                  style={{
                    display: "inline-block",
                    marginTop: 8,
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#B91C1C",
                    textDecoration: "underline",
                  }}
                >
                  Open Settings → Connected Services →
                </a>
              </div>
            </div>
          )}
          {/* Iter 171.D (Danny — bug #6): Title / Subject field at the top
              so reps can paste their preferred booking subject line. */}
          <Section label="Title / Subject">
            <input
              data-testid="m-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. CCM-EHI: Kamilla Ismailoff, MD — 06.12.2026 @ 03:00 pm EST"
              style={inputStyle}
            />
          </Section>

          <Section label="Contact">
            <div style={{ background: "#f9f9f9", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "#444", marginBottom: 8 }}>
              <strong style={{ color: "var(--navy)" }}>{lead?.clinic}</strong> · {lead?.specialty || lead?.nppes?.specialty_group || "—"}
            </div>
            <Label small>Meeting with</Label>
            {/* Iter 142.2 issue #77fb825d — surface every contact on the lead, not just the
                first "primary" entry. Merge `contacts` (multi-contact editor) +
                `decision_makers` (legacy) and de-dupe by name so reps can pick
                any decision-maker without re-entering the data. */}
            <select data-testid="m-with" value={meetingWith} onChange={(e) => setMeetingWith(e.target.value)} style={inputStyle}>
              {(() => {
                const out = []; const seen = new Set();
                const push = (name, role) => {
                  const n = (name || "").trim();
                  if (!n || seen.has(n.toLowerCase())) return;
                  seen.add(n.toLowerCase());
                  out.push({ name: n, role: role || "" });
                };
                // Iter 142.2 review polish — fall back to email/title when name
                // is null/empty so reps can still pick those entries.
                (lead?.contacts || []).forEach((c) => push(c.name || c.full_name || c.email || c.title, c.role || c.title));
                (lead?.decision_makers || []).forEach((c) => push(c.name || c.full_name || c.email || c.title, c.role || c.title));
                if (lead?.contact_name) push(lead.contact_name, lead.contact_role || "primary");
                // EHI client fallback (Iter 258) — when the lead has no
                // structured contacts, surface the client's primary
                // email/phone so the prefilled value has a matching option.
                if (out.length === 0 && clientPrimaryContact) {
                  push(clientPrimaryContact, clientPrimaryContact.includes("@") ? "client email" : "client phone");
                }
                if (out.length === 0) return <option value="">No contacts on file — add via Contacts tab</option>;
                return out.map((c) => (
                  <option key={c.name} value={c.name}>{c.name}{c.role ? ` · ${c.role}` : ""}</option>
                ));
              })()}
            </select>
          </Section>

          {needsClientEmail && (
            <Section label="Client email">
              <input data-testid="m-client-email" type="email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)}
                placeholder="name@practice.com" style={inputStyle} />
              <div data-testid="m-client-email-hint" style={{ fontSize: 11, color: "#A32D2D", marginTop: 4 }}>
                No email on file for this client. Add one so they get the booking confirmation and reminders.
              </div>
            </Section>
          )}

          <Section label="Date & time">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1.4fr", gap: 8 }}>
              <input data-testid="m-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} style={inputStyle} />
              <input data-testid="m-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} step="900" style={inputStyle} />
              <select value={tz} onChange={(e) => setTz(e.target.value)} style={inputStyle}>
                <option>America/New_York</option><option>America/Chicago</option><option>America/Denver</option><option>America/Los_Angeles</option>
              </select>
            </div>
            {/* Iter 327 (Jul 2026, Phase A+B) — Outlook availability
                timeline for the selected Host rep. Renders only when
                the scheduler has M365 connected. Silent no-op when
                connection is missing or Graph errors — the modal
                behaves exactly like it did pre-Iter-327. */}
            {hostEmail && (
              <div data-testid="m-availability" style={{ marginTop: 10 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                  <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4, color: "#666" }}>
                    {hostUser?.full_name || hostEmail}&apos;s Outlook availability
                  </div>
                  {busyLoading && (
                    <span style={{ fontSize: 10, color: "#888", display: "inline-flex", alignItems: "center", gap: 4 }}>
                      <Loader2 style={{ width: 10, height: 10, animation: "spin 1s linear infinite" }} /> Loading…
                    </span>
                  )}
                </div>
                {busyError === "not_connected" ? (
                  <div data-testid="m-availability-not-connected" style={{ fontSize: 10, color: "#92400E", background: "#FEF3C7", border: "0.5px solid #FCD34D", borderRadius: 6, padding: "6px 8px" }}>
                    Connect your Microsoft 365 in Settings → Connected Services to see this rep&apos;s live Outlook availability.
                  </div>
                ) : busyError === "external" ? (
                  <div data-testid="m-availability-external" style={{ fontSize: 10, color: "#666", background: "#F3F4F6", border: "0.5px solid #E5E7EB", borderRadius: 6, padding: "6px 8px" }}>
                    Availability unavailable — this teammate is outside your Microsoft 365 tenant or has hidden free/busy details.
                  </div>
                ) : busyError === "graph_error" ? (
                  <div data-testid="m-availability-error" style={{ fontSize: 10, color: "#666", background: "#F3F4F6", border: "0.5px solid #E5E7EB", borderRadius: 6, padding: "6px 8px" }}>
                    Couldn&apos;t reach Outlook right now — pick any time; conflicts won&apos;t be highlighted.
                  </div>
                ) : (
                  <>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 2, background: "#FAFAFA", border: "0.5px solid #E5E7EB", borderRadius: 8, padding: 6 }}>
                      {timelineCells.map((c) => {
                        const isSel = c.time === time;
                        const styles = {
                          free:              { bg: "#DCFCE7", border: "#86EFAC", color: "#166534", label: "Free" },
                          busy:              { bg: "#FEE2E2", border: "#FCA5A5", color: "#991B1B", label: "Busy" },
                          tentative:         { bg: "#FEF3C7", border: "#FCD34D", color: "#92400E", label: "Tentative" },
                          oof:               { bg: "#DBEAFE", border: "#93C5FD", color: "#1E40AF", label: "Out of office" },
                          workingElsewhere:  { bg: "#E0E7FF", border: "#A5B4FC", color: "#4338CA", label: "Working elsewhere" },
                        }[c.status] || { bg: "#FAFAFA", border: "#E5E7EB", color: "#666", label: c.status };
                        const title = c.subject ? `${styles.label} — ${c.subject}` : styles.label;
                        return (
                          <button
                            key={c.time}
                            type="button"
                            data-testid={`m-slot-${c.time}`}
                            onClick={() => setTime(c.time)}
                            title={`${c.time} · ${title}`}
                            style={{
                              flex: "0 0 auto", padding: "4px 6px", minWidth: 46,
                              borderRadius: 5, fontSize: 10, fontWeight: isSel ? 700 : 500,
                              background: styles.bg, color: styles.color,
                              border: isSel ? `1.5px solid ${styles.color}` : `0.5px solid ${styles.border}`,
                              cursor: "pointer", fontFamily: "inherit",
                              outline: isSel ? "2px solid rgba(24,95,165,0.15)" : "none",
                            }}
                          >
                            {c.time}
                          </button>
                        );
                      })}
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 6, fontSize: 10, color: "#666" }}>
                      <span><span style={{ display: "inline-block", width: 10, height: 10, background: "#DCFCE7", border: "0.5px solid #86EFAC", borderRadius: 3, marginRight: 4, verticalAlign: "middle" }} />Free</span>
                      <span><span style={{ display: "inline-block", width: 10, height: 10, background: "#FEE2E2", border: "0.5px solid #FCA5A5", borderRadius: 3, marginRight: 4, verticalAlign: "middle" }} />Busy</span>
                      <span><span style={{ display: "inline-block", width: 10, height: 10, background: "#FEF3C7", border: "0.5px solid #FCD34D", borderRadius: 3, marginRight: 4, verticalAlign: "middle" }} />Tentative</span>
                      <span><span style={{ display: "inline-block", width: 10, height: 10, background: "#DBEAFE", border: "0.5px solid #93C5FD", borderRadius: 3, marginRight: 4, verticalAlign: "middle" }} />OOO</span>
                    </div>
                    {selectedConflict && (
                      <div data-testid="m-conflict-warning" style={{ marginTop: 8, background: "#FEE2E2", border: "0.5px solid #FCA5A5", borderRadius: 8, padding: "8px 10px", display: "flex", gap: 8, alignItems: "flex-start" }}>
                        <AlertTriangle style={{ width: 14, height: 14, color: "#B91C1C", marginTop: 1, flexShrink: 0 }} />
                        <div style={{ fontSize: 11, color: "#991B1B", lineHeight: 1.4 }}>
                          <strong>Conflict</strong> — {hostUser?.full_name || "This rep"} is {selectedConflict.status === "tentative" ? "tentatively booked" : selectedConflict.status === "oof" ? "out of office" : "busy"} during the selected slot{selectedConflict.subject ? ` (${selectedConflict.subject})` : ""}. Pick a green slot above, or click Save to schedule anyway.
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            <button onClick={() => setShowAITimes((x) => !x)} data-testid="m-ai-times" style={{ background: "transparent", color: "#3C3489", border: "none", fontSize: 11, padding: 0, marginTop: 6, cursor: "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 4 }}>
              <Sparkles style={{ width: 11, height: 11 }} /> AI suggests best times {showAITimes ? "▾" : "▸"}
            </button>
            {showAITimes && (
              <div style={{ background: "#F5F9FF", border: "0.5px solid #B5D4F4", borderRadius: 8, padding: 10, marginTop: 6 }}>
                {aiTimes.map((s, i) => (
                  <button key={i} data-testid={`m-ai-slot-${i}`} onClick={() => { setDate(s.date); setTime(s.time); toast.success(`Slot picked: ${s.date} at ${s.time}`); }} style={{ display: "flex", justifyContent: "space-between", width: "100%", padding: "6px 8px", background: "transparent", border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 12, color: "#185FA5", textAlign: "left" }}>
                    <span><strong>{s.date}</strong> at {s.time}</span>
                    <span style={{ color: "#888", fontSize: 11 }}>{s.note}</span>
                  </button>
                ))}
              </div>
            )}
          </Section>

          <Section label="Meeting details">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
              <div>
                <Label small>Meeting type</Label>
                <select data-testid="m-type" value={type} onChange={(e) => setType(e.target.value)} style={inputStyle}>
                  {MEETING_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <Label small>Host rep</Label>
                {/* Iter 171.D (Danny — bug #7): real users from /api/users/directory, not 3 mock options.
                    Iter 175.7 (Lisa #3): fixed the dead /api/users path so the dropdown actually populates,
                    alphabetized hosts, and defaults to the current rep. */}
                <select data-testid="m-host" value={host} onChange={(e) => setHost(e.target.value)} style={inputStyle}>
                  {users.length === 0 ? (
                    <option value="">Loading hosts…</option>
                  ) : (
                    users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.full_name || u.name || u.email}
                        {u.role ? ` · ${(u.role || "").replace(/_/g, " ")}` : ""}
                      </option>
                    ))
                  )}
                </select>
              </div>
            </div>
            {/* Iter 175.7 (Lisa #4 — `bad77ece`): when Meeting Type =
                "Custom", let the rep describe what the meeting is for.
                The free-text is appended to the meeting subject and the
                calendar invite body so the invitee sees the context. */}
            {type === "Custom" && (
              <div style={{ marginBottom: 8 }}>
                <Label small>Custom meeting description</Label>
                <input
                  data-testid="m-custom-type"
                  value={customType}
                  onChange={(e) => setCustomType(e.target.value)}
                  placeholder="e.g. Implementation kickoff with billing manager"
                  style={inputStyle}
                  autoFocus
                />
              </div>
            )}
            <Label small>
              Meeting link <span style={{ color: "#B91C1C", fontWeight: 700 }} title="Required">*</span>
            </Label>
            <div style={{ display: "flex", gap: 6 }}>
              <input
                data-testid="m-link"
                value={link}
                onChange={(e) => { setLink(e.target.value); if (linkError) setLinkError(""); }}
                placeholder="Paste a RingCentral / Zoom / Teams / Google Meet link, or click Generate →"
                style={{ ...inputStyle, color: "#185FA5", fontFamily: "var(--font-mono, monospace)", fontSize: 11, borderColor: linkError ? "#DC2626" : undefined }}
              />
              {/* Iter 178.2 (Lisa #3fe1558a Feb 2026): one-click RC
                  meeting creation. Calls the backend, which creates a
                  real meeting against the logged-in rep's per-rep RC
                  OAuth token and returns the joinUri. Requires the rep
                  to have connected RC in Settings. */}
              <button
                type="button"
                data-testid="m-generate-rc-link"
                onClick={generateMeetingLink}
                disabled={generatingLink || rcNeedsRepAction}
                title={rcNeedsRepAction
                  ? "Connect your RingCentral in Settings → Connected Services first"
                  : "Create a real RingCentral meeting and fill the link"}
                style={{
                  whiteSpace: "nowrap",
                  background: (generatingLink || rcNeedsRepAction) ? "#E8EEFB" : "#EEEDFE",
                  color: rcNeedsRepAction ? "#94a3b8" : "#3C3489",
                  border: "0.5px solid #C9C7F0",
                  borderRadius: 6,
                  padding: "0 10px",
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: generatingLink ? "wait" : (rcNeedsRepAction ? "not-allowed" : "pointer"),
                  fontFamily: "inherit",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  opacity: rcNeedsRepAction ? 0.65 : 1,
                }}
              >
                {generatingLink
                  ? <Loader2 style={{ width: 11, height: 11 }} className="animate-spin" />
                  : <Video style={{ width: 11, height: 11 }} />}
                {generatingLink ? "Generating…" : "Generate RC link"}
              </button>
            </div>
            {linkError ? (
              <div data-testid="m-link-error" style={{ fontSize: 11, color: "#B91C1C", marginTop: 4, fontWeight: 500 }}>
                {linkError}
              </div>
            ) : (
              <div style={{ fontSize: 10, color: "#888", marginTop: 4 }}>
                <strong style={{ color: "#B91C1C" }}>Required.</strong> Click <strong>Generate RC link</strong> to mint a fresh RingCentral meeting, or paste a link from Zoom / Teams / Meet / Webex.
              </div>
            )}
            <Label small style={{ marginTop: 8 }}>Notes / agenda</Label>
            <textarea data-testid="m-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Add prep notes or agenda items..." style={{ ...inputStyle, minHeight: 70, resize: "vertical", fontFamily: "inherit" }} />
            <button onClick={aiDraftAgenda} style={{ background: "#EEEDFE", color: "#3C3489", border: "none", borderRadius: 6, padding: "4px 10px", fontSize: 11, marginTop: 6, cursor: "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 4 }}>
              {drafting ? <Loader2 style={{ width: 11, height: 11 }} className="animate-spin" /> : <Sparkles style={{ width: 11, height: 11 }} />} Draft agenda with AI
            </button>
            {/* Iter 158 (Josh) — issue #21: invite additional attendees by email.
                Iter 178.2 (Lisa #7e855b8c Feb 2026): added a teammate
                multi-select alongside the free-text emails. Lisa asked
                for a dropdown of names; we kept the free-text input for
                external invitees (e.g. a clinic's billing manager). */}
            <Label small style={{ marginTop: 10 }}>Invite others (optional)</Label>
            {users.length > 0 && (
              <details
                data-testid="m-cc-teammates-wrap"
                style={{
                  border: "0.5px solid #ddd",
                  borderRadius: 8,
                  padding: "6px 10px",
                  marginBottom: 6,
                  background: "#FAFBFD",
                }}
              >
                <summary
                  data-testid="m-cc-teammates-toggle"
                  style={{
                    cursor: "pointer",
                    fontSize: 11,
                    color: "#444",
                    listStyle: "none",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>
                    Pick teammates
                    {ccTeammates.length > 0 && (
                      <span style={{ marginLeft: 6, background: "#3C3489", color: "#fff", borderRadius: 999, padding: "1px 8px", fontSize: 10, fontWeight: 600 }}>
                        {ccTeammates.length} selected
                      </span>
                    )}
                  </span>
                  <span style={{ color: "#888", fontSize: 10 }}>{users.length} available</span>
                </summary>
                <div
                  data-testid="m-cc-teammates-list"
                  style={{
                    marginTop: 8,
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                  }}
                >
                  {/* Iter 253 (Jul 2026) — live search across teammate
                      names + emails. Rows are always sorted A→Z with
                      currently-selected teammates pinned to the top so
                      a quick glance shows who's already invited. */}
                  <input
                    data-testid="m-cc-teammates-search"
                    value={ccSearch}
                    onChange={(e) => setCcSearch(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    placeholder="Search teammates by name or email…"
                    style={{
                      width: "100%",
                      padding: "5px 8px",
                      fontSize: 11,
                      border: "0.5px solid #ddd",
                      borderRadius: 6,
                      fontFamily: "inherit",
                      background: "#fff",
                      outline: "none",
                    }}
                  />
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 4,
                    maxHeight: 220,
                    overflowY: "auto",
                  }}>
                    {(() => {
                      const q = ccSearch.trim().toLowerCase();
                      const rows = users
                        // Iter 253.1 (Jul 2026): host is no longer
                        // filtered out. Some reps genuinely want to
                        // land on their own invite (e.g. so it shows
                        // in their Outlook calendar as an attendee
                        // rather than only as organizer). The
                        // ccFromTeammates→ccEmails merge below already
                        // de-dupes by lowercased email, so no risk of
                        // double-sending.
                        .filter((u) => {
                          if (!q) return true;
                          const name = (u.full_name || u.name || "").toLowerCase();
                          const email = (u.email || "").toLowerCase();
                          return name.includes(q) || email.includes(q);
                        })
                        .map((u) => ({ ...u, _selected: ccTeammates.includes(u.id) }))
                        .sort((a, b) => {
                          // Selected first, then alphabetical.
                          if (a._selected !== b._selected) return a._selected ? -1 : 1;
                          const an = (a.full_name || a.name || a.email || "").toLowerCase();
                          const bn = (b.full_name || b.name || b.email || "").toLowerCase();
                          return an.localeCompare(bn);
                        });
                      if (rows.length === 0) {
                        return (
                          <div
                            data-testid="m-cc-teammates-empty"
                            style={{ gridColumn: "1 / -1", fontSize: 11, color: "#888", padding: "10px 6px", textAlign: "center" }}
                          >
                            No teammates match “{ccSearch}”
                          </div>
                        );
                      }
                      return rows.map((u) => {
                        // Iter 346 — availability colour for EVERY shown
                        // teammate (not just selected). free=light-green
                        // box, busy=light-red box, unknown=neutral grey box
                        // (+tooltip). While the lookup is in flight, rows
                        // show a subtle "checking…" state. The selected
                        // (checkbox) highlight sits on top of the colour.
                        const em = (u.email || "").trim().toLowerCase();
                        // Iter 346.1 — only colour when the SCHEDULER is
                        // actually M365-connected (m365Ready). If not, the
                        // whole feature stays silent (plain rows) per spec —
                        // rather than a misleading wall of grey "unknown".
                        // Grey "unknown" is reserved for teammates we truly
                        // can't resolve while the scheduler IS connected.
                        const availOn = m365Ready && !!date && !!time && !!em;
                        const checking = availOn && teammateBusyLoading;
                        const status = availOn ? (teammateAvailByEmail[em] || "unknown") : undefined;
                        // Row box background + border by availability.
                        let boxBg = "transparent";
                        let boxBorder = "0.5px solid transparent";
                        let rowTitle = u.email || "";
                        if (!checking && status === "free") {
                          boxBg = "#ecfdf5"; boxBorder = "0.5px solid #a7f3d0";
                        } else if (!checking && status === "busy") {
                          boxBg = "#fef2f2"; boxBorder = "0.5px solid #fecaca";
                        } else if (!checking && status === "unknown") {
                          boxBg = "#f3f4f6"; boxBorder = "0.5px solid #e5e7eb";
                          rowTitle = "Availability unknown";
                        }
                        // Selected state keeps its purple tint on top.
                        if (u._selected) {
                          boxBg = status === "busy" ? "#fde8e8"
                                : status === "free" ? "#dcfce7"
                                : "#EEEDFE";
                        }
                        const availAttr = checking ? "checking" : (status || "none");
                        return (
                        <label
                          key={u.id}
                          data-testid={`m-cc-teammate-${u.id}`}
                          data-avail={availAttr}
                          title={rowTitle}
                          style={{
                            display: "flex",
                            gap: 6,
                            alignItems: "center",
                            padding: "5px 7px",
                            borderRadius: 6,
                            background: boxBg,
                            border: boxBorder,
                            fontSize: 11,
                            cursor: "pointer",
                            transition: "background-color 0.15s ease",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={u._selected}
                            onChange={() => toggleCcTeammate(u.id)}
                            style={{ accentColor: "#3C3489", cursor: "pointer" }}
                          />
                          <span
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              color: "#1f2937",
                              fontWeight: (status === "busy" || status === "free") ? 600 : 400,
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                              minWidth: 0,
                            }}
                          >
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                              {u.full_name || u.name || u.email}
                            </span>
                            {checking && (
                              <Loader2 size={11} style={{ animation: "spin 1s linear infinite", flexShrink: 0, color: "#6b7280" }} />
                            )}
                          </span>
                        </label>
                        );
                      });
                    })()}
                  </div>
                </div>
              </details>
            )}
            <input
              data-testid="m-cc-emails"
              value={ccEmails}
              onChange={(e) => setCcEmails(e.target.value)}
              placeholder="Or add external emails: manager@practice.com, colleague@practice.com"
              style={inputStyle}
            />
            <div style={{ fontSize: 10, color: "#888", marginTop: 4 }}>Pick teammates above + paste external emails here. Everyone gets a calendar invite with the meeting link.</div>
          </Section>

          {type === "Product demo" && (
            <Section label="Products to demo">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {[...new Set([...PRODUCTS, ...(lead?.extra_products || []), ...(lead?.products_pitched || [])])].map((p) => {
                  const active = products.includes(p);
                  return (
                    <button key={p} onClick={() => setProducts((arr) => arr.includes(p) ? arr.filter((x) => x !== p) : [...arr, p])} style={{ border: "0.5px solid", borderColor: active ? "#2563eb" : "#ddd", background: active ? "#E6F1FB" : "#fff", color: active ? "#185FA5" : "#222", borderRadius: 999, padding: "4px 10px", fontSize: 11, fontWeight: 500, cursor: "pointer", fontFamily: "inherit" }}>{p}</button>
                  );
                })}
              </div>
            </Section>
          )}
        </div>

        <footer style={{ padding: "12px 20px", borderTop: "0.5px solid #eee", display: "flex", gap: 8, justifyContent: "flex-end", background: "#FAFBFD" }}>
          <button onClick={onClose} style={btnOutline}>Cancel</button>
          <button data-testid="m-save" onClick={handleSave} style={btnPrimary}><Check style={{ width: 13, height: 13 }} /> Schedule meeting</button>
        </footer>
      </div>
    </div>
  );
}

const inputStyle = { width: "100%", border: "0.5px solid #ddd", borderRadius: 8, padding: "7px 10px", fontSize: 13, fontFamily: "inherit", outline: "none", color: "#222", background: "#fff", boxSizing: "border-box" };
const btnOutline = { background: "#fff", color: "#222", border: "0.5px solid #ddd", borderRadius: 8, padding: "6px 14px", fontSize: 12, cursor: "pointer", fontFamily: "inherit" };
const btnPrimary = { background: "#2563eb", color: "#fff", border: "none", borderRadius: 8, padding: "6px 16px", fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 6 };

function Section({ label, children }) { return <div style={{ marginBottom: 14 }}><Label>{label}</Label>{children}</div>; }
function Label({ children, small, style }) { return <div style={{ fontSize: small ? 9 : 10, textTransform: "uppercase", color: "#888", letterSpacing: "0.04em", fontWeight: 600, marginBottom: 6, ...style }}>{children}</div>; }
